import { PaymentMethod, PaymentStatus } from '@lombok/shared';
import type {
  PaymentMethod as PaymentMethodType,
  PaymentStatus as PaymentStatusType,
} from '@lombok/shared';
import type { SelectOption } from '@/components/form';

/**
 * Spanish labels + groupings for the Pagos slice. Stored values stay snake_case
 * (from @lombok/shared); only what users see is Spanish. Status pill colors live
 * in StatusPill (shared map) — these are the payment-specific extras: the method
 * labels, the method <select> options, the list filter tabs, and a guard for the
 * proof-file upload.
 */

/** Payment method code → Spanish label. Unknown codes fall back to the raw code. */
export const PAYMENT_METHOD_LABELS_ES: Record<PaymentMethodType, string> = {
  pago_movil: 'Pago Móvil',
  zelle: 'Zelle',
  transferencia: 'Transferencia',
  efectivo: 'Efectivo',
  punto_de_venta: 'Punto de venta',
  nota_de_credito: 'Nota de crédito',
  ajuste_manual: 'Ajuste manual',
  stripe: 'Tarjeta (Stripe)',
};

/** Resolve a payment method code to its Spanish label (raw code as fallback). */
export function paymentMethodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS_ES[method as PaymentMethodType] ?? method;
}

/**
 * Methods a customer/sales/distributor user can self-report. Excludes the
 * internal-only adjustments (nota_de_credito, ajuste_manual) and Stripe (which
 * settles automatically, not via a manually uploaded proof). Order puts the
 * common Venezuela methods first.
 */
export const SELF_REPORT_METHODS: readonly PaymentMethodType[] = [
  PaymentMethod.PAGO_MOVIL,
  PaymentMethod.TRANSFERENCIA,
  PaymentMethod.ZELLE,
  PaymentMethod.PUNTO_DE_VENTA,
  PaymentMethod.EFECTIVO,
];

/** <select> options (value + Spanish label) for the self-report payment form. */
export const PAYMENT_METHOD_OPTIONS: readonly SelectOption[] = SELF_REPORT_METHODS.map(
  (value) => ({ value, label: PAYMENT_METHOD_LABELS_ES[value] }),
);

/** A list filter tab: a label + the status it filters by (`undefined` = "Todos"). */
export interface PaymentStatusTab {
  label: string;
  status?: PaymentStatusType;
}

/**
 * Filter tabs for the Pagos list. Order puts the actionable review state
 * (pendiente) first, then the resolved states.
 */
export const PAYMENT_STATUS_TABS: readonly PaymentStatusTab[] = [
  { label: 'Pendiente', status: PaymentStatus.PENDING },
  { label: 'Aprobado', status: PaymentStatus.APPROVED },
  { label: 'Rechazado', status: PaymentStatus.REJECTED },
];

/**
 * Accept set for the payment-proof upload: images (a photo of the receipt) and
 * PDFs. Mirrors the `accept` attribute on the file input so the runtime guard
 * and the picker agree.
 */
export const PROOF_ACCEPT = 'image/png,image/jpeg,image/webp,image/heic,application/pdf';

const ALLOWED_PROOF_PREFIXES = ['image/'] as const;
const ALLOWED_PROOF_EXACT = ['application/pdf'] as const;

/**
 * Guard the proof file's MIME type before we ask the backend for a presigned
 * URL. We only allow an image (receipt photo/screenshot) or a PDF — never an
 * arbitrary binary. Empty/unknown types are rejected so the user picks a real
 * file. This is a UX guard only; the backend re-validates the content type.
 */
export function isAllowedProofContentType(contentType: string | undefined | null): boolean {
  if (!contentType) return false;
  const type = contentType.trim().toLowerCase();
  if (type === '') return false;
  if (ALLOWED_PROOF_EXACT.includes(type as (typeof ALLOWED_PROOF_EXACT)[number])) return true;
  return ALLOWED_PROOF_PREFIXES.some((prefix) => type.startsWith(prefix));
}
